import { useAuth } from 'hooks'
import { ActivityLogBody } from 'pages/settings/ActivityLogPage'

/**
 * Personal → Activity (VRG-338) — the same company audit log as Settings → Activity
 * Log, pinned to the signed-in user's own actions. Shares its entire list/search/
 * sort/filter/export body with the company page via `ActivityLogBody`; see that
 * file's docblock for the company/personal split, and the `ActivityLogBody`
 * component doc for what changes (role filter + Actor/Role columns drop; the
 * actor is pinned, never user-editable).
 *
 * ⚠️ Gating note (requirement 6): this page carries NO `permission` in
 * `SettingsPage`'s PERSONAL_MENU — reading your OWN activity needs no company-wide
 * grant, so it must not be gated on `activity_log_view` (the COMPANY page's gate),
 * which a non-admin (e.g. a Design hire) would not hold.
 *
 * There IS a real remaining constraint, on the API side, that this page cannot paper
 * over: `GET /companies/admin/me/activity` (shared by both pages) is guarded by
 * `@RequireAdmin()` on the backend. Every event this feed can contain is itself an
 * admin action (company_updated, user_added, role_added, …), so a non-admin has zero
 * rows here in practice regardless — but today a non-admin hitting this page gets a
 * 403 from the query rather than a clean "no activity yet" empty state, because the
 * hook has no error branch. That's pre-existing backend behaviour this task does not
 * change (no backend work was in scope) — flagged rather than silently left unnoticed.
 */
export const PersonalActivityPage = () => {
  const { profile } = useAuth()

  // Guard against the (normally instantaneous) window before the WorkOS profile has
  // resolved — never fire the query with an empty actorUserId, which would silently
  // read as "no actor filter" server-side rather than "this user's own activity".
  if (!profile?.id) return null

  return <ActivityLogBody fixedActorId={profile.id} />
}
